import { useMemo, useState } from 'react';
import { Slider } from '@c2fo/liquidity';
import useLocaleFormat from '@/utils/useLocaleFormat';

export interface RangeSliderProps {
  onChange: (value: number) => void;
  suggested: {
    rate: number;
    lowSlider: number;
    highSlider: number;
  };
  value: number;
}

const formatValue = (num: number) => Math.round(num * 100) / 100;

const NameYourRateSlider = ({
  onChange,
  suggested: { rate: suggestedRate, lowSlider: rawLowSlider, highSlider: rawHighSlider },
  value,
}: RangeSliderProps) => {
  const { asNumber } = useLocaleFormat();
  const [open, setOpen] = useState(false);
  const lowSlider = useMemo(() => formatValue(rawLowSlider), [rawLowSlider]);
  const highSlider = useMemo(() => formatValue(rawHighSlider), [rawHighSlider]);
  const localValue = useMemo(() => formatValue(Number(value)), [value]);
  const boundary = useMemo(() => {
    /**
     * determine the largest diff from suggestion between the low/high slider values.
     * for example, given these inputs:
     *  suggestion = 2
     *  lowSlider = 1
     *  highSlider = 4
     * the return value would be 2.
     * this allows us to calculate an appropriate area/spacing on the slider bar on each extreme
     * to "pad" the suggestion area that will flex with any given suggestion data.
     */
    const lowDiff = suggestedRate - lowSlider;
    const highDiff = highSlider - suggestedRate;
    return Math.abs(lowDiff > highDiff ? lowDiff : highDiff);
  }, [suggestedRate, lowSlider, highSlider]);

  const sliderMinRaw = lowSlider - boundary;
  const sliderMaxRaw = highSlider + boundary;
  const sliderMin = sliderMinRaw > 0 ? formatValue(sliderMinRaw) : 0; // don't show a negative rate
  const sliderMax = formatValue(sliderMaxRaw);

  const guidanceOuterPct = useMemo(() => {
    if (sliderMax === sliderMin) {
      return 0;
    }

    return ((formatValue(lowSlider) - sliderMin) / (sliderMax - sliderMin)) * 100;
  }, [lowSlider, sliderMin, sliderMax]);

  const shouldShowTooltip = localValue > sliderMin && localValue < sliderMax && sliderMax !== sliderMin;

  const tooltipPosition = useMemo(() => {
    if (sliderMax === sliderMin) {
      return 0;
    }

    return ((localValue - sliderMin) / (sliderMax - sliderMin)) * 100;
  }, [localValue, sliderMin, sliderMax]);

  return (
    <Slider.Root
      className="relative flex h-5 touch-none select-none items-center pb-7"
      min={sliderMin}
      max={sliderMax}
      onValueChange={(value) => onChange(value[0])}
      step={0.01}
      value={[localValue]}
    >
      <Slider.Track className="relative h-2 grow rounded-full bg-transparent">
        <div className="flex w-full gap-1">
          <div className="h-2 rounded-full bg-gray-200" style={{ width: `${guidanceOuterPct}%` }} />
          <div
            className="flex h-2 justify-between rounded-full bg-lightBlue-500"
            style={{ width: `${100 - guidanceOuterPct * 2}%` }}
          >
            <div className="pt-3 text-sm font-medium">{`${asNumber({ value: lowSlider, decimals: 2 })}%`}</div>
            <div className="pt-3 text-sm font-medium">{`${asNumber({ value: highSlider, decimals: 2 })}%`}</div>
          </div>
          <div className="h-2 rounded-full bg-gray-200" style={{ width: `${guidanceOuterPct}%` }} />
        </div>
        <Slider.Range className="absolute h-full rounded-full bg-transparent" />
      </Slider.Track>
      {open && shouldShowTooltip && (
        <div
          className="pointer-events-none absolute -top-12 z-10 flex items-center justify-center"
          style={{
            left: `${tooltipPosition}%`,
            transform: 'translateX(-50%)',
          }}
        >
          <div className="relative">
            <div className="whitespace-nowrap rounded-md bg-deepGray-500 p-2 text-sm text-white shadow-md">
              {`${asNumber({ value, decimals: 2 })}%`}
            </div>
            <div className="absolute left-1/2 top-full -translate-x-1/2">
              <div className="h-0 w-0 border-x-4 border-t-4 border-transparent border-t-deepGray-500"></div>
            </div>
          </div>
        </div>
      )}
      <Slider.Thumb
        className="block h-6 w-6 cursor-pointer rounded-full border-[3px] border-lightBlue-500 bg-white shadow-md focus:outline-none"
        onPointerEnter={() => setOpen(true)}
        onPointerLeave={() => setOpen(false)}
      />
    </Slider.Root>
  );
};

export default NameYourRateSlider;
